import { useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { Toaster, toast } from "react-hot-toast";
import { loginRequest, apiScopes } from "./auth/msalConfig";
import { InteractionStatus } from "@azure/msal-browser";
import { CameraPage } from "./pages/CameraPage";
import { DetectionsPage } from "./pages/DetectionsPage";
import { DevicesPage } from "./pages/DevicesPage";
import { OrchestratorPage } from "./pages/OrchestratorPage";
import { ReviewPage } from "./pages/ReviewPage";
import { TestersPage } from "./pages/TestersPage";
import { theme, tint, applyTheme, type ThemeMode } from "./theme";
import { isDevAuth } from "./auth/devAuth";
import {
  ApiError, getLocalSession, isLocalAuth, localAuthApi, setLocalSession, updateLocalUser,
} from "./auth/localAuth";
import { ChangePasswordDialog } from "./components/ChangePasswordDialog";
import { AppContext, type RouterInfo } from "./context/AppContext";
import { AppShell } from "./components/Sidebar";

const API_BASE     = import.meta.env.VITE_API_BASE_URL as string;
import { useWebSocket } from "./hooks/useWebSocket";
import intelliportLogo from "./assets/intelliport-logo.svg";
import intelliportLogoDark from "./assets/intelliport-logo-dark.svg";

// ── Detection toast notification ────────────────────────────────────────────

interface ToastDetection {
  id: string;
  detection_events: { type_name: string }[];
  confidence_score: number;
  device_serial: string | null;
  timestamp: string;
  ml_verdict: string | null;   // central-ML verdict (real/false_alarm/error/null)
  duplicate_reason: string | null;  // "no_change" / "previous" / "too_dark", or null
}

function DetectionToast({ d, onDismiss, visible }: { d: ToastDetection; onDismiss: () => void; visible: boolean }) {
  // Central-ML false positive → greyed-out toast. The tints below go through
  // tint(): theme.* are var() strings, and a var() cannot carry an appended
  // alpha suffix — see theme.ts.
  const isFalse   = d.ml_verdict === "false_alarm";
  const isSafety  = d.detection_events?.some((e) => e.type_name.toLowerCase().includes("helmet"));
  const category  = isFalse ? "False positive" : isSafety ? "Safety" : "Disappearance";
  const color     = isFalse ? theme.textMuted : isSafety ? theme.warning : theme.danger;
  const confidence = Math.round(d.confidence_score * 100);
  const ts = new Date(d.timestamp).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const labelText = isFalse
    ? "Central model: no anomaly confirmed"
    : (d.detection_events ?? []).map((e) => e.type_name).join(", ");

  return (
    <div
      className={visible ? "toast-in" : "toast-out"}
      style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 8,
        padding: "10px 12px 10px 14px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        width: 272,
        pointerEvents: "auto",
        opacity: isFalse ? 0.72 : 1,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Row 1: badge + confidence */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <span style={{
            background: tint(color, 13), color,
            border: `1px solid ${tint(color, 33)}`,
            borderRadius: 3, padding: "1px 7px",
            fontSize: 10, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: "0.06em",
            whiteSpace: "nowrap",
          }}>
            {category}
          </span>
          <span style={{ color: isFalse ? theme.textMuted : theme.gold, fontSize: 12, fontWeight: 600 }}>{confidence}%</span>
        </div>
        {/* Row 2: label */}
        <div style={{ fontSize: 13, color: isFalse ? theme.textMuted : theme.textPrimary, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {labelText}
        </div>
        {/* Row 3: device + time */}
        <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
          {d.device_serial && <span style={{ fontFamily: "monospace" }}>{d.device_serial}</span>}
          {d.device_serial && " · "}
          {ts}
        </div>
      </div>
      <button
        onClick={onDismiss}
        style={{
          background: "transparent", border: "none",
          cursor: "pointer", color: theme.textMuted,
          padding: "2px 4px", lineHeight: 1, fontSize: 14,
          flexShrink: 0, marginTop: 1,
        }}
      >
        ✕
      </button>
    </div>
  );
}

const LOGIN_INPUT: CSSProperties = {
  width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 5,
  border: `1px solid ${theme.borderStrong}`, background: theme.bgInput,
  color: theme.textPrimary, fontFamily: "inherit", fontSize: 14, outline: "none",
  textAlign: "left",
};

/** Email + password form for external testers, revealed under the Microsoft
 *  button. On success the session is stored and the page reloads so main.tsx
 *  runs again with the MSAL instance patched by applyLocalAuth — the simplest
 *  way to get every token call site onto the new token. */
function LocalLoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canSubmit = !busy && email.trim().length > 0 && password.length > 0;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await localAuthApi.login(email.trim(), password);
      setLocalSession(resp);
      location.reload();
      // Keep the button disabled while the reload takes over.
    } catch (err) {
      // 401 / 429 / 503 all carry a human-readable `detail`; show it verbatim.
      setError(err instanceof ApiError ? err.message : "Could not reach the server — please try again.");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: "grid", gap: 10, marginTop: 14, textAlign: "left" }}>
      <input
        type="email" name="email" autoComplete="username" placeholder="Email" autoFocus required
        value={email} disabled={busy} onChange={(e) => { setEmail(e.target.value); setError(null); }}
        style={LOGIN_INPUT}
      />
      <input
        type="password" name="password" autoComplete="current-password" placeholder="Password" required
        value={password} disabled={busy} onChange={(e) => { setPassword(e.target.value); setError(null); }}
        style={LOGIN_INPUT}
      />
      {error && (
        <div role="alert" style={{
          color: theme.danger, background: theme.dangerSoft, border: `1px solid ${theme.danger}`,
          borderRadius: 5, padding: "8px 10px", fontSize: theme.fontSize2Xs, lineHeight: 1.4,
        }}>
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={!canSubmit}
        style={{
          background: theme.bgCard, color: theme.textPrimary, fontFamily: "inherit",
          border: `1px solid ${theme.borderStrong}`, borderRadius: theme.radius, padding: "11px 0",
          fontSize: 14, fontWeight: 700, cursor: canSubmit ? "pointer" : "default", width: "100%",
          letterSpacing: "0.04em", opacity: canSubmit ? 1 : 0.6,
        }}
      >
        {busy ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
}

function LoginPage() {
  const { instance, inProgress } = useMsal();
  const isRedirecting = inProgress === InteractionStatus.HandleRedirect ||
                        inProgress === InteractionStatus.Login;
  const isDark = localStorage.getItem(DARK_MODE_KEY) === "true" ||
    (localStorage.getItem(DARK_MODE_KEY) === null && window.matchMedia("(prefers-color-scheme: dark)").matches);
  const loginLogo = isDark ? intelliportLogoDark : intelliportLogo;
  const [showEmail, setShowEmail] = useState(false);
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: theme.bgBase }}>
      <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 10, padding: "48px 52px", textAlign: "center", maxWidth: 400, width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
        <img src={loginLogo} alt="Intelliport" style={{ height: 36, marginBottom: 28, filter: "none" }} />
        <h2 style={{ color: theme.textPrimary, margin: "0 0 8px", fontWeight: 600, fontSize: 22 }}>Incident Detection AI Demo</h2>
        <p style={{ color: theme.textSecondary, margin: "0 0 32px", fontSize: 15 }}>Anomaly Detection Camera System</p>
        <button
          onClick={() => instance.loginRedirect(loginRequest)}
          disabled={isRedirecting}
          style={{ background: theme.gold, color: theme.onGold, fontFamily: "inherit", border: "none", borderRadius: theme.radius, padding: "12px 0", fontSize: 14, fontWeight: 700, cursor: isRedirecting ? "default" : "pointer", width: "100%", letterSpacing: "0.04em", opacity: isRedirecting ? 0.7 : 1 }}
          onMouseEnter={(e) => { if (!isRedirecting) (e.currentTarget as HTMLButtonElement).style.background = theme.goldLight; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = theme.gold; }}
        >
          {isRedirecting ? "Redirecting…" : "Sign in with Microsoft"}
        </button>

        {/* External testers have no Microsoft account; they get an email +
            password issued by staff on the Testers page. Kept behind a second
            click so the primary path stays the one-button screen it was. */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "18px 0" }}>
          <span style={{ flex: 1, height: 1, background: theme.border }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: theme.textMuted }}>or</span>
          <span style={{ flex: 1, height: 1, background: theme.border }} />
        </div>
        {showEmail ? (
          <LocalLoginForm />
        ) : (
          <button
            type="button"
            onClick={() => setShowEmail(true)}
            disabled={isRedirecting}
            style={{
              background: "transparent", color: theme.textPrimary, fontFamily: "inherit",
              border: `1px solid ${theme.borderStrong}`, borderRadius: theme.radius, padding: "11px 0",
              fontSize: 14, fontWeight: 700, cursor: isRedirecting ? "default" : "pointer", width: "100%",
              letterSpacing: "0.04em", opacity: isRedirecting ? 0.7 : 1,
            }}
          >
            Sign in with email
          </button>
        )}
      </div>
    </div>
  );
}

const DARK_MODE_KEY = "ips-dark-mode";

// Authenticated shell — holds the WebSocket and device state at the top level
function AuthenticatedApp() {
  const { instance, accounts } = useMsal();
  const [deviceId, setDeviceId] = useState<string>("");
  const [routers, setRouters] = useState<RouterInfo[]>([]);
  const { lastMessage, readyState } = useWebSocket();

  // Dark mode — persisted in localStorage
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem(DARK_MODE_KEY);
    return saved ? saved === "true" : window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  useEffect(() => {
    const mode: ThemeMode = darkMode ? "dark" : "light";
    applyTheme(mode);
    localStorage.setItem(DARK_MODE_KEY, String(darkMode));
  }, [darkMode]);

  // Apply initial theme synchronously before first paint
  useEffect(() => {
    applyTheme(darkMode ? "dark" : "light");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleDarkMode = () => setDarkMode((v) => !v);

  // External testers who signed in with a temporary password (first login or
  // an admin reset) must pick their own before the app is usable. The flag is
  // read from the stored session and re-checked against /me on mount, which
  // also doubles as an early revocation check (a 401 here signs them out via
  // the fetch guard in localAuth).
  const [mustChangePassword, setMustChangePassword] = useState<boolean>(
    () => isLocalAuth() && (getLocalSession()?.user.must_change_password ?? false)
  );
  useEffect(() => {
    const session = isLocalAuth() ? getLocalSession() : null;
    if (!session) return;
    localAuthApi.me(session.token)
      .then((me) => {
        updateLocalUser({ name: me.name, roles: me.roles, must_change_password: me.must_change_password });
        setMustChangePassword(me.must_change_password);
      })
      .catch(() => { /* offline or transient — keep what the login response said */ });
  }, []);

  // Fetch devices on mount
  useEffect(() => {
    if (!accounts.length) return;
    instance
      .acquireTokenSilent({ scopes: apiScopes, account: accounts[0] })
      .then((r) =>
        fetch(`${API_BASE}/api/devices`, {
          headers: { Authorization: `Bearer ${r.accessToken}` },
        })
      )
      .then((resp) => (resp.ok ? resp.json() : Promise.reject(resp.status)))
      .then((data: RouterInfo[]) => {
        setRouters(data);
        if (!deviceId && data.length > 0) {
          setDeviceId(data[0].serial_number);
        }
      })
      .catch((err) => console.error("Failed to fetch devices:", err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle WS messages for device events
  useEffect(() => {
    if (!lastMessage) return;
    const msg = lastMessage as Record<string, unknown>;

    if (msg.type === "device_status") {
      const serial = msg.serial as string;
      const online = msg.online as boolean;
      const lastSeen = (msg.last_seen as string) ?? null;
      // model_name/model_version/impulse_id ride along on every device_status
      // broadcast (not just device_registered) so a router reconnecting after a
      // model hot-swap updates the Devices page's "Up to date"/"Mismatch" badge
      // live, without a page reload — see device_manager.upsert_from_rest.
      const hasModelFields = "model_version" in msg;
      setRouters((prev) =>
        prev.map((r) =>
          r.serial_number === serial
            ? {
                ...r,
                online,
                last_seen: lastSeen,
                ...(hasModelFields
                  ? {
                      model_name: (msg.model_name as string | null) ?? null,
                      model_version: (msg.model_version as string | null) ?? null,
                      impulse_id: (msg.impulse_id as number | null) ?? null,
                    }
                  : {}),
              }
            : r
        )
      );
    } else if (msg.type === "device_registered") {
      const device = msg.device as RouterInfo;
      setRouters((prev) => {
        const exists = prev.some((r) => r.id === device.id);
        return exists ? prev : [...prev, device];
      });
    } else if (msg.type === "device_deleted") {
      const serial = msg.serial as string;
      setRouters((prev) => prev.filter((r) => r.serial_number !== serial));
    } else if (msg.type === "new_detection" && msg.data) {
      const d = msg.data as ToastDetection;
      // Toast for central-confirmed real anomalies (full colour) and central-ML
      // false positives (greyed, see DetectionToast). Suppress only "error":
      // it carries the raw, unconfirmed router data and would be misleading.
      // When central eval is disabled (ml_verdict null, legacy) we still toast.
      // Duplicates are announced so the dashboard's counters stay live, but a
      // toast is a demand for attention and a repeat of an already-seen scene
      // has none to make. Announcing without interrupting is the whole point of
      // separating the two.
      const suppressToast = d.ml_verdict === "error" || Boolean(d.duplicate_reason);
      if (!suppressToast) {
        toast.custom(
          (t) => (
            <DetectionToast
              d={d}
              onDismiss={() => toast.dismiss(t.id)}
              visible={t.visible}
            />
          ),
          { duration: 5000, position: "bottom-right" }
        );
      }
    }
  }, [lastMessage]);

  return (
    <AppContext.Provider value={{ deviceId, setDeviceId, lastMessage, wsReadyState: readyState, routers, setRouters, darkMode, toggleDarkMode }}>
      {/* AppShell owns the whole frame — rail beside content on desktop, a top
          bar above it with an off-canvas rail on mobile. Keeping that decision
          inside one component means the routes never have to know which of the
          two layouts they are sitting in. */}
      <AppShell>
        <Routes>
          <Route path="/" element={<CameraPage />} />
          <Route path="/detections" element={<DetectionsPage />} />
          <Route path="/devices" element={<DevicesPage />} />
          <Route path="/orchestrator" element={<OrchestratorPage />} />
          <Route path="/review/:taskId" element={<ReviewPage />} />
          <Route path="/testers" element={<TestersPage />} />
        </Routes>
      </AppShell>
      {mustChangePassword && (
        <ChangePasswordDialog forced onChanged={() => setMustChangePassword(false)} />
      )}
    </AppContext.Provider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <div style={{
        minHeight: "100vh",
        background: theme.bgBase,
        fontFamily: theme.fontSans,
        fontSize: theme.fontSizeBase,
        color: theme.textPrimary,
      }}>
        <Toaster
          position="bottom-right"
          toastOptions={{ style: { background: theme.bgCard, color: theme.textPrimary, border: `1px solid ${theme.border}` } }}
        />
        {isDevAuth() || isLocalAuth() ? (
          // Two non-Entra principals skip the MSAL login gate entirely: dev/test
          // loop engineering (API/WS calls carry DEV_API_TOKEN via applyDevAuth)
          // and external testers signed in with email + password (their JWT via
          // applyLocalAuth). Either way the patched instance hands out the token.
          <AuthenticatedApp />
        ) : (
          <>
            <AuthenticatedTemplate>
              <AuthenticatedApp />
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
              <LoginPage />
            </UnauthenticatedTemplate>
          </>
        )}
      </div>
    </BrowserRouter>
  );
}
